import { z } from "zod";
import {
  CURRENCIES,
  PRIORITIES,
  PROJECT_CAPABILITIES,
  PROJECT_STATUSES,
  type ProjectCapability,
  type ProjectMemberRole,
  type ProjectStatus,
} from "../status.js";
import { dateOnlySchema, versionSchema } from "./common.js";

const userIdListSchema = z
  .array(z.string().uuid())
  .transform((ids) => [...new Set(ids)]);
const moneyStringSchema = z
  .string()
  .regex(/^\d{1,12}(\.\d{1,2})?$/, "Amount must be a non-negative decimal");

function validateMembershipOverlap(
  value: { managerIds: string[]; memberIds: string[] },
  ctx: z.RefinementCtx,
): void {
  const managers = new Set(value.managerIds);
  const overlap = value.memberIds.find((id) => managers.has(id));
  if (overlap) {
    ctx.addIssue({
      code: "custom",
      path: ["memberIds"],
      message: "A user cannot be both a manager and a member",
    });
  }
}

export const projectCreateSchema = z
  .object({
    customerId: z.string().uuid(),
    parentId: z.string().uuid().optional(),
    sourceRequestId: z.string().uuid().optional(),
    /** An accepted offer this project delivers; linked in the same step. */
    sourceOfferId: z.string().uuid().optional(),
    title: z.string().trim().min(1).max(180),
    description: z.string().trim().max(6000).optional(),
    priority: z.enum(PRIORITIES).default("normal"),
    plannedStart: dateOnlySchema.optional(),
    plannedEnd: dateOnlySchema.optional(),
    plannedMinutes: z.number().int().min(0).default(0),
    currency: z.enum(CURRENCIES).default("CHF"),
    internalNotes: z.string().trim().max(4000).optional(),
    managerIds: userIdListSchema,
    memberIds: userIdListSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    validateMembershipOverlap(value, ctx);
    if (
      value.plannedStart &&
      value.plannedEnd &&
      value.plannedEnd < value.plannedStart
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["plannedEnd"],
        message: "Planned end must not be before planned start",
      });
    }
  });
export type ProjectCreateDto = z.infer<typeof projectCreateSchema>;

export const projectUpdateSchema = z
  .object({
    version: versionSchema,
    parentId: z.string().uuid().nullable().optional(),
    title: z.string().trim().min(1).max(180).optional(),
    description: z.string().trim().max(6000).nullable().optional(),
    priority: z.enum(PRIORITIES).optional(),
    plannedStart: dateOnlySchema.nullable().optional(),
    plannedEnd: dateOnlySchema.nullable().optional(),
    plannedMinutes: z.number().int().min(0).optional(),
    internalNotes: z.string().trim().max(4000).nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (
      value.plannedStart &&
      value.plannedEnd &&
      value.plannedEnd < value.plannedStart
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["plannedEnd"],
        message: "Planned end must not be before planned start",
      });
    }
  });
export type ProjectUpdateDto = z.infer<typeof projectUpdateSchema>;

export const projectStatusSchema = z
  .object({
    version: versionSchema,
    status: z.enum(PROJECT_STATUSES),
    comment: z.string().trim().max(1000).optional(),
  })
  .strict();
export type ProjectStatusDto = z.infer<typeof projectStatusSchema>;

export const projectMembershipReplaceSchema = z
  .object({
    version: versionSchema,
    managerIds: userIdListSchema,
    memberIds: userIdListSchema,
  })
  .strict()
  .superRefine(validateMembershipOverlap);
export type ProjectMembershipReplaceDto = z.infer<
  typeof projectMembershipReplaceSchema
>;

export const projectBudgetSchema = z
  .object({
    version: versionSchema,
    revenueBudget: moneyStringSchema,
    laborBudget: moneyStringSchema,
    materialBudget: moneyStringSchema,
    expenseBudget: moneyStringSchema,
    contingency: moneyStringSchema,
  })
  .strict();
export type ProjectBudgetDto = z.infer<typeof projectBudgetSchema>;

export const projectCostCreateSchema = z
  .object({
    kind: z.enum(["material", "subcontractor", "other"]),
    date: dateOnlySchema,
    description: z.string().trim().min(1).max(500),
    amount: moneyStringSchema,
  })
  .strict();
export type ProjectCostCreateDto = z.infer<typeof projectCostCreateSchema>;

export const projectCostUpdateSchema = projectCostCreateSchema
  .partial()
  .extend({ version: versionSchema })
  .strict();
export type ProjectCostUpdateDto = z.infer<typeof projectCostUpdateSchema>;

export const projectAssignmentSchema = z
  .object({
    version: versionSchema,
    projectId: z.string().uuid().nullable(),
  })
  .strict();
export type ProjectAssignmentDto = z.infer<typeof projectAssignmentSchema>;

export const projectRolePermissionsSchema = z
  .object({
    manager: z.array(z.enum(PROJECT_CAPABILITIES)),
    member: z.array(z.enum(PROJECT_CAPABILITIES)),
  })
  .strict()
  .transform((value) => ({
    manager: [...new Set(value.manager)],
    member: [...new Set(value.member)],
  }));
export type ProjectRolePermissionsDto = z.infer<
  typeof projectRolePermissionsSchema
>;

export interface ProjectSummary {
  id: string;
  number: string;
  title: string;
  customerId: string;
  status: ProjectStatus;
  priority: string;
  currency: string;
  plannedStart?: string;
  plannedEnd?: string;
  version: number;
}

export interface ProjectMemberResponse {
  userId: string;
  firstName: string;
  lastName: string;
  role: ProjectMemberRole;
}

export interface ProjectCapabilitiesResponse {
  effectiveCapabilities: ProjectCapability[];
}
